import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowLeftIcon,
  MailIcon,
  PhoneIcon,
  UserIcon,
  LockIcon,
  CheckIcon,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import Button from '../components/Button'
import GlassPanel from '../components/GlassPanel'
import { getErrorMessage } from '../utils/errorHandler'
import { api } from '../services/api'
import { useTheme } from '../context/ThemeContext'

interface RegisterFormData {
  phone: string
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  termsAgreed: boolean
}

interface LoginFormData {
  phone: string
  password: string
}

// Initial form data
const initialFormData: RegisterFormData = {
  phone: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  termsAgreed: false,
}

const Register: React.FC = () => {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [step, setStep] = useState<'form' | 'otp' | 'login'>('form')

  // Initialize form data from session storage
  const [formData, setFormData] = useState<RegisterFormData>(() => {
    const saved = sessionStorage.getItem('registerFormData')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return {
          ...initialFormData,
          phone: parsed.phone || '',
          firstName: parsed.firstName || '',
          lastName: parsed.lastName || '',
          email: parsed.email || '',
          termsAgreed: parsed.termsAgreed || false,
        }
      } catch (e) {
        console.error('Error parsing saved form data', e)
        return initialFormData
      }
    }
    return initialFormData
  })

  const [loginData, setLoginData] = useState<LoginFormData>({
    phone: '',
    password: '',
  })

  const [otp, setOtp] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [otpTimer, setOtpTimer] = useState(300) // 5 minutes in seconds
  const [otpExpired, setOtpExpired] = useState(false)

  // Timer effect for OTP expiration
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (step === 'otp' && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setOtpExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      clearInterval(interval);
    };
  }, [step, otpTimer]);

  // Format timer as mm:ss
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Save form data whenever it changes
  useEffect(() => {
    const dataToSave = {
      phone: formData.phone,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      termsAgreed: formData.termsAgreed,
      // Don't save passwords for security
    }
    sessionStorage.setItem('registerFormData', JSON.stringify(dataToSave))
  }, [formData])

  // Save form data when user leaves page
  useEffect(() => {
    // Function to handle page unload
    const handleBeforeUnload = () => {
      const dataToSave = {
        phone: formData.phone,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        termsAgreed: formData.termsAgreed,
      }
      sessionStorage.setItem('registerFormData', JSON.stringify(dataToSave))
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      handleBeforeUnload(); // Save when component unmounts
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [formData])

  // Theme styles
  const bgGradient = theme === 'light' 
    ? 'linear-gradient(to bottom right, #F7F9FB, #D6E4FF)' 
    : 'linear-gradient(to bottom right, #2D3436, #0D0F1A)'
  
  const labelColor = theme === 'light' ?  'text-gray-700' : 'text-gray-300'
  const inputBgColor = theme === 'light' ? 'bg-white' : 'bg-gray-800'
  const inputBorderColor = theme === 'light' ? 'border-gray-300' : 'border-gray-700'
  const inputTextColor = theme === 'light' ? 'text-gray-800' : 'text-gray-200'
  const iconColor = theme === 'light' ? 'text-gray-500' : 'text-gray-400'
  const accentColor = 'ring-[#4B7BFF]'
  const panelBgColor = theme === 'light' ? 'bg-white/80' : 'bg-gray-800/80'
  const panelBorderColor = theme === 'light' ? 'border-gray-200' : 'border-gray-700'
  const linkColor = theme === 'light' ? 'text-[#4B7BFF]' : 'text-[#6B9CFF]'
  const checkboxBgColor = theme === 'light' ? 'bg-[#4B7BFF]' : 'bg-[#6B9CFF]'

  // ----------------------------------
  // Handlers
  // ----------------------------------
  const handleRegisterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }
      // Immediately save to session storage on each change
      const dataToSave = {
        phone: newData.phone,
        firstName: newData.firstName,
        lastName: newData.lastName,
        email: newData.email,
        termsAgreed: newData.termsAgreed,
      }
      sessionStorage.setItem('registerFormData', JSON.stringify(dataToSave))
      return newData
    })
    setError(null)
  }

  const handleLoginInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }))
    setError(null)
  }

  // Register Submit
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    let phoneNumber = formData.phone.startsWith('+')
      ? formData.phone.slice(1)
      : formData.phone
    formData.phone = phoneNumber

    // Basic validation
    if (!phoneNumber.match(/^[1-9][0-9]{10,}$/)) {
      setError('يرجى إدخال رقم هاتف بصيغة صحيحة مع رمز الدولة بدون صفر')
      setLoading(false)
      return
    }
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('يرجى إدخال عنوان بريد إلكتروني صالح')
      setLoading(false)
      return
    }
    if (formData.password.length < 8 || !formData.password.match(/[A-Z]/)) {
      setError(
        'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل وتحتوي على حرف كبير واحد على الأقل'
      )
      setLoading(false)
      return
    }
    if (formData.confirmPassword !== formData.password) {
      setError('يجب أن تكون كلمة المرور متطابقة')
      setLoading(false)
      return
    }
    if (!formData.termsAgreed) {
      setError('يجب الموافقة على الشروط وسياسة الخصوصية للمتابعة')
      setLoading(false)
      return
    }

    try {
      await api.verifyRegister({ phone: formData.phone })
      setStep('otp')
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  // OTP Submit
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const response = await api.registerUser({
        ...formData,
        otp,
      })

      if (response.accessToken) {
        localStorage.setItem('token', response.accessToken)
        localStorage.setItem('refreshToken', response.refreshToken)
        // Clear stored form data after successful registration
        sessionStorage.removeItem('registerFormData')
        navigate('/')
      } else {
        setError('فشل التسجيل. يرجى المحاولة مرة أخرى.')
      }
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  // Login Submit
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    let phoneNumber = loginData.phone.startsWith('+')
      ? loginData.phone.slice(1)
      : loginData.phone
    if (!phoneNumber.match(/^[1-9][0-9]{10,}$/)) {
      setError('يرجى إدخال رقم هاتف بصيغة صحيحة مع رمز الدولة بدون صفر')
      setLoading(false)
      return
    }
    if (loginData.password.length < 8) {
      setError('يجب أن تكون كلمة المرور 8 أحرف على الأقل')
      setLoading(false)
      return
    }
    try {
      loginData.phone = phoneNumber
      const response = await api.loginWithPhoneAndPass(loginData)

      if (response.accessToken) {
        localStorage.setItem('token', response.accessToken)
        localStorage.setItem('refreshToken', response.refreshToken)
        navigate('/')
      } else {
        setError('فشل تسجيل الدخول. يرجى التحقق من بياناتك.')
      }
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  // Navigate to Terms/Privacy with state preservation
  const handleNavigation = (path: string) => {
    // Save current form data before navigating
    const dataToSave = {
      phone: formData.phone,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      termsAgreed: formData.termsAgreed,
    }
    sessionStorage.setItem('registerFormData', JSON.stringify(dataToSave))
    navigate(path)
  }

  // ----------------------------------
  // Render
  // ----------------------------------
  return (
    <div
      className="relative h-screen w-screen flex flex-col p-4 md:p-8 overflow-auto"
      style={{
        background: bgGradient,
      }}
    >
      {/* Floating shapes */}
      <motion.div
        className="absolute top-10 left-[15%] w-16 h-16 bg-[#4B7BFF]/20 rounded-full z-0"
        animate={{ y: [0, 25, 0], x: [0, -15, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute top-[55%] right-[10%] w-24 h-24 bg-[#8E44AD]/20 rounded-full z-0"
        animate={{ y: [0, -20, 0], x: [0, 20, 0] }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
      />

      {/* Back Button */}
      <div className="mb-8 z-10">
        <Link to="/">
          <Button variant="secondary" className="px-4 py-2" icon={<ArrowLeftIcon size={16} />}>
            رجوع
          </Button>
        </Link>
      </div>

      {/* Main Container */}
      <motion.div
        className="relative z-10 w-full max-w-md mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <GlassPanel className={`p-6 ${panelBgColor} ${panelBorderColor} backdrop-blur-sm shadow-md`}>
          <motion.h1
            className="text-3xl font-bold text-center mb-6 text-transparent bg-clip-text bg-gradient-to-r from-[#4B7BFF] to-[#8E44AD]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {step === 'form'
              ? 'إنشاء حساب'
              : step === 'otp'
              ? 'التحقق من رمز OTP'
              : 'تسجيل الدخول'}
          </motion.h1>

          {/* Error Message */}
          {error && (
            <motion.div
              className="bg-red-500/20 border border-red-500/50 text-red-600 dark:text-red-400 px-4 py-2 rounded-lg mb-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.div>
          )}

          {/* STEP 1: Registration Form */}
          {step === 'form' && (
            <>
              <form onSubmit={handleRegisterSubmit} className="space-y-4">
                {/* Phone */}
                <div className="space-y-2">
                  <label className={labelColor}>رقم الهاتف</label>
                  <div className="relative">
                    <PhoneIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconColor}`} size={20} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleRegisterInputChange}
                      className={`w-full ${inputBgColor} border ${inputBorderColor} rounded-lg pl-10 pr-4 py-2 ${inputTextColor} focus:outline-none focus:ring-2 focus:${accentColor}`}
                      placeholder="966555555555"
                      required
                    />
                  </div>
                </div>

                {/* First/Last name */}
                <div className="grid grid-cols-2 gap-4">
                  {/* First Name */}
                  <div className="space-y-2">
                    <label className={labelColor}>الاسم الأول</label>
                    <div className="relative">
                      <UserIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconColor}`} size={20} />
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleRegisterInputChange}
                        className={`w-full ${inputBgColor} border ${inputBorderColor} rounded-lg pl-10 pr-4 py-2 ${inputTextColor} focus:outline-none focus:ring-2 focus:${accentColor}`}
                        placeholder="الاسم الأول"
                        required
                      />
                    </div>
                  </div>
                  {/* Last Name */}
                  <div className="space-y-2">
                    <label className={labelColor}>الاسم الأخير</label>
                    <div className="relative">
                      <UserIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconColor}`} size={20} />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleRegisterInputChange}
                        className={`w-full ${inputBgColor} border ${inputBorderColor} rounded-lg pl-10 pr-4 py-2 ${inputTextColor} focus:outline-none focus:ring-2 focus:${accentColor}`}
                        placeholder="الاسم الأخير"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className={labelColor}>البريد الإلكتروني</label>
                  <div className="relative">
                    <MailIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconColor}`} size={20} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleRegisterInputChange}
                      className={`w-full ${inputBgColor} border ${inputBorderColor} rounded-lg pl-10 pr-4 py-2 ${inputTextColor} focus:outline-none focus:ring-2 focus:${accentColor}`}
                      placeholder="examble@gmail.com"
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className={labelColor}>كلمة المرور</label>
                  <div className="relative">
                    <LockIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconColor}`} size={20} />
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleRegisterInputChange}
                      className={`w-full ${inputBgColor} border ${inputBorderColor} rounded-lg pl-10 pr-4 py-2 ${inputTextColor} focus:outline-none focus:ring-2 focus:${accentColor}`}
                      placeholder="كلمة المرور"
                      required
                    />
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className={labelColor}>تأكيد كلمة المرور</label>
                  <div className="relative">
                    <LockIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconColor}`} size={20} />
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleRegisterInputChange}
                      className={`w-full ${inputBgColor} border ${inputBorderColor} rounded-lg pl-10 pr-4 py-2 ${inputTextColor} focus:outline-none focus:ring-2 focus:${accentColor}`}
                      placeholder="تأكيد كلمة المرور"
                      required
                    />
                  </div>
                </div>

                {/* Terms and Privacy Checkbox - Modern RTL style */}
                <div className="flex flex-row-reverse items-start mt-6">
                  <div className="relative">
                    <input
                      id="terms"
                      name="termsAgreed"
                      type="checkbox"
                      checked={formData.termsAgreed}
                      onChange={handleRegisterInputChange}
                      className="sr-only"
                      required
                    />
                    <div 
                      className={`flex h-5 w-5 items-center justify-center rounded border ${
                        formData.termsAgreed 
                          ? `${checkboxBgColor} border-transparent` 
                          : theme === 'light' ? 'border-gray-300 bg-white' : 'border-gray-600 bg-gray-700'
                      } transition-colors cursor-pointer`}
                      onClick={() => setFormData(prev => ({ ...prev, termsAgreed: !prev.termsAgreed }))}
                    >
                      {formData.termsAgreed && (
                        <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                      )}
                    </div>
                  </div>
                  <label 
                    htmlFor="terms" 
                    className={`${labelColor} cursor-pointer select-none text-sm mr-3 text-right`}
                    onClick={() => setFormData(prev => ({ ...prev, termsAgreed: !prev.termsAgreed }))}
                  >
                    أوافق على{' '}
                    <button 
                      type="button" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigation('/terms');
                      }}
                      className={`${linkColor} hover:underline`}
                    >
                      الشروط والأحكام
                    </button>{' '}
                    و{' '}
                    <button 
                      type="button" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigation('/privacy');
                      }}
                      className={`${linkColor} hover:underline`}
                    >
                      سياسة الخصوصية
                    </button>
                  </label>
                </div>

                <Button variant="primary" className="w-full mt-6" disabled={loading}>
                  {loading ? 'جارٍ إرسال رمز OTP...' : 'متابعة'}
                </Button>
              </form>

              <Button
                variant="secondary"
                className="w-full mt-4"
                onClick={() => setStep('login')}
              >
                لديك حساب بالفعل؟ تسجيل الدخول
              </Button>
            </>
          )}

          {/* STEP 2: OTP */}
          {step === 'otp' && (
            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className={labelColor}>أدخل رمز OTP</label>
                  <div className={`text-sm font-medium ${
                    otpTimer <= 60 ? 'text-red-500' : (theme === 'light' ? 'text-[#4B7BFF]' : 'text-[#6B9CFF]')
                  }`}>
                    <span className="inline-flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {formatTime(otpTimer)}
                    </span>
                  </div>
                </div>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
                    setOtp(value)
                  }}
                  className={`w-full ${inputBgColor} border ${inputBorderColor} rounded-lg px-4 py-2 ${inputTextColor} focus:outline-none focus:ring-2 focus:${accentColor}`}
                  placeholder="أدخل رمز OTP المكون من 6 أرقام"
                  maxLength={6}
                  required
                  disabled={otpExpired}
                />
                {otpExpired && (
                  <p className="text-red-500 text-sm">
                    انتهت صلاحية الرمز. 
                    <button 
                      type="button" 
                      className={`${linkColor} mr-1 hover:underline`}
                      onClick={() => {
                        setOtpTimer(300);
                        setOtpExpired(false);
                        setOtp('');
                        // Resend OTP logic would go here
                      }}
                    >
                      إعادة إرسال الرمز
                    </button>
                  </p>
                )}
              </div>

              <Button variant="primary" className="w-full mt-6" disabled={loading || otpExpired}>
                {loading ? 'جارٍ التحقق...' : 'التحقق من رمز OTP'}
              </Button>
            </form>
          )}

          {/* STEP 3: Login */}
          {step === 'login' && (
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className={labelColor}>رقم الهاتف</label>
                <div className="relative">
                  <PhoneIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconColor}`} size={20} />
                  <input
                    type="tel"
                    name="phone"
                    value={loginData.phone}
                    onChange={handleLoginInputChange}
                    className={`w-full ${inputBgColor} border ${inputBorderColor} rounded-lg pl-10 pr-4 py-2 ${inputTextColor} focus:outline-none focus:ring-2 focus:${accentColor}`}
                    placeholder="+966000000000"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={labelColor}>كلمة المرور</label>
                <div className="relative">
                  <LockIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconColor}`} size={20} />
                  <input
                    type="password"
                    name="password"
                    value={loginData.password}
                    onChange={handleLoginInputChange}
                    className={`w-full ${inputBgColor} border ${inputBorderColor} rounded-lg pl-10 pr-4 py-2 ${inputTextColor} focus:outline-none focus:ring-2 focus:${accentColor}`}
                    placeholder="كلمة المرور"
                    required
                  />
                </div>
              </div>

              <Button variant="primary" className="w-full mt-6" disabled={loading}>
                {loading ? 'جارٍ تسجيل الدخول...' : 'تسجيل الدخول'}
              </Button>
              <Button
                variant="secondary"
                className="w-full mt-4"
                onClick={() => setStep('form')}
              >
                ليس لديك حساب ؟ 
              </Button>
            </form>
          )}
        </GlassPanel>
      </motion.div>
    </div>
  )
}

export default Register
